import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  deployApi,
  socioApi,
  type ContractField,
  type ContractManifest,
  type Deployment,
  type SocioMe,
  ApiError,
} from "@/api/client";
import { FieldInput } from "@/components/FieldInput";

/**
 * Tela de deploy avançado: cards por contrato com descrição + form
 * tipado por kind de cada constructor arg.
 *
 * Fluxo:
 *   1. Lista de cards agrupados por categoria
 *   2. Click no card → painel à direita com form parametrizado
 *   3. Submit chama deployApi.deploy convertendo os valores tipados
 *      pra constructorArgs (string[]) que o backend espera
 *
 * Pra criar tokens + vesting de um projeto inteiro de uma vez, use
 * a tela `/admin/projects/new` em vez desta — esta é pra deploys
 * individuais (ex: deployar só o EntryPoint).
 */
export default function Deploy() {
  const [manifests, setManifests] = useState<ContractManifest[]>([]);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [selected, setSelected] = useState<ContractManifest | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [label, setLabel] = useState("");
  const [me, setMe] = useState<SocioMe | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<Deployment | null>(null);

  useEffect(() => {
    Promise.all([
      deployApi.manifests().then((r) => setManifests(r.manifests)),
      deployApi.list().then((r) => setDeployments(r.deployments)),
      socioApi.me().then((r) => setMe(r)).catch(() => undefined),
    ]).catch((err) => setError(err.message));
  }, []);

  const grouped = useMemo(() => {
    const g: Record<string, ContractManifest[]> = {};
    for (const m of manifests) {
      (g[m.category] ||= []).push(m);
    }
    return g;
  }, [manifests]);

  function resolveDefault(token: string): string {
    if (token === "@deployer") {
      // Como o gateway não expõe o address do deployer pro frontend ainda,
      // usamos a smartAccount do sócio logado como fallback razoável.
      return me?.smartAccount ?? "0x...";
    }
    if (token === "@now") {
      return String(Math.floor(Date.now() / 1000));
    }
    return token;
  }

  function selectContract(m: ContractManifest) {
    setSelected(m);
    setLastResult(null);
    setError(null);
    const initial: Record<string, string> = {};
    for (const f of m.fields) {
      if (f.defaultValue) {
        initial[f.name] = f.defaultValue.startsWith("@")
          ? resolveDefault(f.defaultValue)
          : f.defaultValue;
      } else {
        initial[f.name] = "";
      }
    }
    setValues(initial);
  }

  async function onDeploy() {
    if (!selected) return;
    setError(null);
    setLoading(true);
    setLastResult(null);

    try {
      const constructorArgs = selected.fields.map((f) => fieldToArg(f, values[f.name] ?? ""));
      const result = await deployApi.deploy({
        contractName: selected.name,
        constructorArgs: constructorArgs as (string | number | boolean | string[])[],
        label: label || undefined,
      });
      setLastResult(result);
      setLabel("");
      const fresh = await deployApi.list();
      setDeployments(fresh.deployments);
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : err.message ?? "Erro inesperado");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <Link to="/dashboard" className="text-sm underline">← voltar</Link>
      <h1 className="text-2xl font-bold my-4">Deploy avançado</h1>
      <p className="text-sm text-gray-600 mb-6">
        Pra criar um projeto inteiro (token + vesting + sócios), use{" "}
        <Link to="/admin/projects/new" className="text-blue-700 underline">
          Novo projeto
        </Link>
        . Esta tela é pra deployar um contrato individual (ex: EntryPoint, Paymaster).
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna 1: lista de contratos agrupados */}
        <section className="lg:col-span-1 space-y-4">
          <h2 className="text-lg font-semibold">Contratos</h2>
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <h3 className="text-xs uppercase text-gray-500 mb-1">{categoryLabel(cat)}</h3>
              <div className="space-y-2">
                {list.map((m) => (
                  <button
                    key={m.name}
                    type="button"
                    onClick={() => selectContract(m)}
                    className={`block w-full text-left border rounded-lg p-3 transition ${
                      selected?.name === m.name
                        ? "border-black bg-black text-white"
                        : "hover:border-gray-400"
                    }`}
                  >
                    <p className="font-medium">{m.title}</p>
                    <p className={`text-xs mt-1 ${
                      selected?.name === m.name ? "text-gray-200" : "text-gray-500"
                    }`}>{m.shortDescription}</p>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* Coluna 2-3: form do contrato selecionado */}
        <section className="lg:col-span-2">
          {!selected ? (
            <div className="border-dashed border-2 border-gray-200 rounded-lg p-12 text-center text-gray-400">
              Selecione um contrato à esquerda
            </div>
          ) : (
            <div className="border rounded-lg p-5">
              <div className="mb-4">
                <h2 className="text-xl font-semibold">{selected.title}</h2>
                <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">
                  {selected.fullDescription}
                </p>
              </div>

              {selected.fields.length === 0 ? (
                <p className="text-sm text-gray-500 italic mb-4">
                  Este contrato não precisa de argumentos.
                </p>
              ) : (
                <div className="space-y-4 mb-4">
                  {selected.fields.map((f) => (
                    <div key={f.name}>
                      <label className="block text-sm font-medium">
                        {f.label}{" "}
                        <span className="text-gray-400 font-mono text-xs">({f.name})</span>
                      </label>
                      {f.description && (
                        <p className="text-xs text-gray-500 mt-0.5 mb-1">{f.description}</p>
                      )}
                      <FieldInput
                        field={f}
                        value={values[f.name] ?? ""}
                        onChange={(v) => setValues((p) => ({ ...p, [f.name]: v }))}
                        resolveDefault={resolveDefault}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium">Label (opcional)</label>
                <input
                  type="text" value={label} onChange={(e) => setLabel(e.target.value)}
                  placeholder="ex: bootstrap-v1"
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded p-3 mb-3 text-sm whitespace-pre-wrap">
                  {error}
                </div>
              )}
              {lastResult && (
                <div className="bg-green-50 border border-green-200 rounded p-3 mb-3 text-sm">
                  ✅ Deployado em{" "}
                  <a
                    href={`https://sepolia.basescan.org/address/${lastResult.address}`}
                    target="_blank" rel="noreferrer"
                    className="font-mono text-xs underline"
                  >
                    {lastResult.address}
                  </a>
                </div>
              )}

              <button
                type="button"
                onClick={onDeploy}
                disabled={loading}
                className="bg-black text-white rounded px-6 py-2 disabled:opacity-50"
              >
                {loading ? "Deployando..." : "Deploy"}
              </button>
            </div>
          )}
        </section>
      </div>

      {/* Histórico */}
      <section className="border rounded-lg p-4 mt-6">
        <h2 className="text-lg font-semibold mb-3">Histórico de deploys</h2>
        {deployments.length === 0 ? (
          <p className="text-sm text-gray-500">Nenhum deploy ainda.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-gray-500">
                <th className="py-2">Contrato</th>
                <th>Endereço</th>
                <th>Bloco</th>
                <th>Em</th>
                <th>Label</th>
              </tr>
            </thead>
            <tbody>
              {deployments.map((d) => (
                <tr key={d.txHash} className="border-b last:border-0">
                  <td className="py-2 font-semibold">{d.contractName}</td>
                  <td className="font-mono text-xs">
                    <a target="_blank" rel="noreferrer" className="underline"
                       href={`https://sepolia.basescan.org/address/${d.address}`}>
                      {d.address}
                    </a>
                  </td>
                  <td>{d.blockNumber}</td>
                  <td>{new Date(d.deployedAt).toLocaleString("pt-BR")}</td>
                  <td className="text-gray-500">{d.label ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

// ─── Conversão de valores tipados pra args do construtor ────────────────

function fieldToArg(field: ContractField, raw: string): string | number | boolean | string[] {
  const v = raw.trim();
  switch (field.kind) {
    case "bool":
      return v === "true";
    case "address":
      return v;
    case "address-list":
      return v.split(",").map((s) => s.trim()).filter(Boolean);
    case "tokens": {
      // Escala 10^18
      const n = BigInt(v || "0");
      return (n * 10n ** 18n).toString();
    }
    case "uint256":
    case "timestamp":
      return v || "0";
    case "duration-months": {
      // converte meses → segundos
      const months = Number(v || "0");
      const seconds = Math.round(months * 30 * 24 * 60 * 60);
      return String(seconds);
    }
    case "duration-seconds":
      return v || "0";
    case "string":
    case "symbol":
      return v;
  }
}

function categoryLabel(c: string): string {
  switch (c) {
    case "token": return "Token";
    case "vesting": return "Vesting";
    case "investor": return "Investidores";
    case "aa": return "Account Abstraction (ERC-4337)";
    case "governance": return "Governança";
    default: return c;
  }
}
